import { formatDate } from "@helpers/formatDate"
import {
	Dispatch,
	FC,
	ReactElement,
	SetStateAction,
	cloneElement,
	forwardRef,
	isValidElement,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react"
import { useReactToPrint } from "react-to-print"
import { ContentWrapper, IContentWrapperProps, findSpanOutOf60 } from "./ContentWrapper"
import {
	IReportGeneratorTrigger,
	ReportGeneratorTrigger,
	ReportGeneratorTriggerOnLoading,
} from "./ReportGeneratorTrigger"
import { IReportPageBodyProps } from "./ReportPageBody"
import { IReportPageFooterProps } from "./ReportPageFooterDipai"
import { IReportPageHeaderProps } from "./ReportPageHeaderDipai"
import { ReportStyle } from "./ReportStyle"
import {
	ContentProps,
	PlacedContentProps,
	REPORT_PAGE_GRID_COLS,
	REPORT_PAGE_GRID_ROWS,
	convertGridRowOffsetToPixels,
	convertGridRowsToPixels,
	divideIntoPages,
} from "./reportGeneratorUtils"

export interface IReportGeneratorProps {
	children: ReactElement<ReactElementExtended>[]
	customerLogo: ReactElement
	dipaiLogo: ReactElement
	pdfTitle?: string
	reportName: string
	dateFrom: string
	dateTo: string
	listOfVessels: string[]
	autoPrintKey?: number | string
	isDisabled?: boolean
	onAfterPrint?: () => void
	randomIdKey?: number
}

interface IGenericReportGeneratorProps extends IReportGeneratorProps {
	ReportPageBody: FC<IReportPageBodyProps>
	ReportPageHeader: FC<IReportPageHeaderProps>
	ReportPageFooter: FC<IReportPageFooterProps>
}

interface IPreparedComponent {
	idKey: string
	size: ContentProps
	renderChild: ReactElement<ReactElementExtended>
	overflowRenderChild: ReactElement<ReactElementExtended>
}

export interface ReactElementExtended {
	[key: string]: unknown
	exposeIsLoaded?: Dispatch<
		SetStateAction<
			{
				idKey: string
				isLoaded: boolean | undefined | null
			}[]
		>
	>
	idKey?: string
}
const ReportGenerator = forwardRef<HTMLDivElement, IGenericReportGeneratorProps>((props, ref) => {
	// MISSING LOGIC TO SCALE COMPONENTS
	const [itemsLoaded, setItemsLoaded] = useState<
		{ idKey: string; isLoaded: boolean | undefined | null }[]
	>([])
	const isLoadingRef = useRef(itemsLoaded)

	useEffect(() => {
		isLoadingRef.current = itemsLoaded
	}, [itemsLoaded])

	const [components, setComponents] = useState<IPreparedComponent[]>([])
	const [isDataLoading, setIsDataLoading] = useState<boolean>(false)

	const [TriggerElement, setTriggerElement] = useState<ReactElement<IReportGeneratorTrigger>>()
	const [TriggerOnLoadingElement, setTriggerOnLoading] =
		useState<ReactElement<IReportGeneratorTrigger>>()
	const [rerenderTrigger, setRerenderTrigger] = useState(0)

	useEffect(() => {
		const itemsLoadedTemp: { idKey: string; isLoaded: boolean | undefined | null }[] = []
		const componentsTemp: IPreparedComponent[] = []
		let nextTriggerElement: ReactElement<IReportGeneratorTrigger> | undefined
		let nextTriggerOnLoadingElement: ReactElement<IReportGeneratorTrigger> | undefined

		props.children.forEach((child) => {
			if (isValidElement(child)) {
				if (child.type !== ContentWrapper) {
					if (child.type === ReportGeneratorTrigger) {
						nextTriggerElement = child as ReactElement<IReportGeneratorTrigger>
					} else if (child.type === ReportGeneratorTriggerOnLoading) {
						nextTriggerOnLoadingElement = child as ReactElement<IReportGeneratorTrigger>
					} else {
						console.error(
							"All children of MainComponent must be wrapped in a ContentWrapper components."
						)
					}
				} else {
					const {
						idKey,
						rowSpan: rowPercentage,
						colSpan: colPercentage,
						pageBreakpoints,
						children: childChildren,
					} = child.props as unknown as IContentWrapperProps

					if (childChildren !== null && childChildren !== undefined) {
						itemsLoadedTemp.push({
							isLoaded: itemsLoaded.find((l) => l.idKey === idKey)?.isLoaded,
							idKey,
						})

						componentsTemp.push({
							idKey,
							size: {
								key: idKey,
								w: findSpanOutOf60(colPercentage, { clampToPage: true }),
								h: findSpanOutOf60(rowPercentage),
								pageBreakpoints,
							},
							renderChild: cloneElement(
								childChildren as ReactElement<ReactElementExtended>,
								{
									exposeIsLoaded: setItemsLoaded,
									idKey,
								}
							),
							overflowRenderChild: cloneElement(
								childChildren as ReactElement<ReactElementExtended>,
								{}
							),
						})
					}
				}
			}
		})

		setComponents(componentsTemp)
		setItemsLoaded(itemsLoadedTemp)
		setTriggerElement(nextTriggerElement)
		setTriggerOnLoading(nextTriggerOnLoadingElement)
	}, [props.children, rerenderTrigger])
	// PRINTING
	const componentRef = useRef<HTMLDivElement>(null)

	const onBeforeGetContentResolve = useRef<unknown>(null)
	const [isRendered, setIsRendered] = useState<boolean>(false)

	const handleOnBeforeGetContent = async () => {
		const ref = componentRef.current
		if (ref === null) {
			return
		}
		setIsDataLoading(true)
		setIsRendered(true)

		await new Promise<void>((resolve, reject) => {
			setTimeout(() => {
				onBeforeGetContentResolve.current = resolve

				let attempts = 0
				const maxAttempts = 1000000

				const checkIfLoaded = () => {
					const loadingStatusArray = isLoadingRef.current.map((i) => i.isLoaded)
					if (
						loadingStatusArray.includes(false) === false &&
						loadingStatusArray.includes(true) === true
					) {
						resolve()
					} else if (attempts < maxAttempts) {
						attempts += 1
						setTimeout(checkIfLoaded, 250)
					} else {
						setIsRendered(false)
						setIsDataLoading(false)
						console.error(`Loading timed out after ${maxAttempts} attempts`)
						alert("Cannot print because there was an error loading the content.")
						reject(
							new Error(
								"Cannot print because there was an error loading the content."
							)
						)
					}
				}
				checkIfLoaded()
			}, 1000)
		})
	}

	const handleAfterPrint = useCallback(() => {
		const ref = componentRef.current
		if (ref === null) {
			return
		}
		return new Promise((resolve: (value: void) => void) => {
			// setItemsLoaded((prev) =>
			// 	prev.map((i) => {
			// 		return { ...i, isLoaded: undefined }
			// 	})
			// )
			setIsRendered(false)
			setIsDataLoading(false)

			if (props.onAfterPrint !== undefined) {
				props.onAfterPrint()
			}

			resolve()
		})
	}, [props])

	const handlePrint = useReactToPrint({
		contentRef: componentRef,
		documentTitle: props.pdfTitle,
		nonce: undefined,
		onBeforePrint: handleOnBeforeGetContent,
		onAfterPrint: handleAfterPrint,
		preserveAfterPrint: false,
	})

	useEffect(() => {
		if (components.length !== itemsLoaded.length) {
			setRerenderTrigger((prev) => prev + 1)
		}
	}, [components.length, itemsLoaded.length])

	const componentLayoutsByPage = useMemo(() => {
		return divideIntoPages(
			components.map((component) => component.size),
			REPORT_PAGE_GRID_COLS,
			REPORT_PAGE_GRID_ROWS
		)
	}, [components])

	const dateStrLocal = formatDate(new Date(), "DD/MM/YYYY - HH:MM")
	const totalPages = componentLayoutsByPage.length

	const handlePrintOnClick = useCallback(() => {
		handlePrint()
	}, [handlePrint])

	const isAutoPrintReady =
		components.length > 0 &&
		itemsLoaded.length === components.length &&
		itemsLoaded.every((item) => item.isLoaded === true)
	const lastAutoPrintKeyRef = useRef<IReportGeneratorProps["autoPrintKey"]>(undefined)
	// created by AI
	useEffect(() => {
		if (
			props.autoPrintKey === undefined ||
			props.autoPrintKey === lastAutoPrintKeyRef.current ||
			props.isDisabled === true ||
			isAutoPrintReady === false
		) {
			return
		}

		lastAutoPrintKeyRef.current = props.autoPrintKey
		handlePrintOnClick()
	}, [handlePrintOnClick, isAutoPrintReady, props.autoPrintKey, props.isDisabled])

	const hasAnyLoadedComponent = itemsLoaded.some((item) => item.isLoaded === true)
	const componentMap = useMemo(
		() => new Map(components.map((component) => [component.idKey, component])),
		[components]
	)

	const renderComponentLayout = useCallback(
		(layout: PlacedContentProps) => {
			const component = componentMap.get(layout.key)
			if (component === undefined) {
				return null
			}

			const componentLoadState = itemsLoaded.find(
				(item) => item.idKey === layout.key
			)?.isLoaded
			const shouldRenderError =
				hasAnyLoadedComponent === true && componentLoadState === undefined

			const content = shouldRenderError ? (
				<div className="h-full w-full bg-slate-200">
					{"Error: Component does not contain all the necessary logic for printing"}
				</div>
			) : layout.sliceOffsetRows === 0 ? (
				cloneElement(component.renderChild)
			) : (
				cloneElement(component.overflowRenderChild)
			)

			const wrapperStyle = {
				gridColumn: `${layout.colStart + 1} / span ${layout.w}`,
				gridRow: `${layout.rowStart + 1} / span ${layout.h}`,
			}

			if (layout.sliceOffsetRows === 0 && layout.h === layout.totalRows) {
				return (
					<div
						key={layout.placementKey}
						className="overflow-hidden"
						style={wrapperStyle}
					>
						{content}
					</div>
				)
			}

			const fullHeightPx = convertGridRowsToPixels(layout.totalRows)
			const sliceOffsetPx = convertGridRowOffsetToPixels(layout.sliceOffsetRows)

			return (
				<div
					key={layout.placementKey}
					className="overflow-hidden"
					style={wrapperStyle}
				>
					<div
						className="w-full"
						style={{
							height: `${fullHeightPx}px`,
							transform: `translateY(-${sliceOffsetPx}px)`,
							transformOrigin: "top left",
						}}
					>
						{content}
					</div>
				</div>
			)
		},
		[componentMap, hasAnyLoadedComponent, itemsLoaded]
	)

	return (
		<>
			{/* <div className="absolute -top-[25vh] -left-50 h-[200vh] scale-50 overflow-scroll bg-red-500"> */}
			<div className="absolute -top-250000 -left-250000">
				<div
					ref={componentRef}
					className={`h-auto w-450 flex-col overflow-hidden`}
				>
					{componentLayoutsByPage.length > 0 ? (
						<>
							<ReportStyle
								marginTop={"25px"}
								marginBottom={"25px"}
							/>
							{/* PAGE ELEMENT */}
							{componentLayoutsByPage.map((pageContent, idx) => (
								<div
									key={`reportPage_${idx}`}
									className={`relative flex h-[calc(2604px)] flex-col gap-3`}
									style={{
										pageBreakAfter: idx === totalPages - 1 ? "auto" : "always",
										breakAfter: idx === totalPages - 1 ? "auto" : "page",
										pageBreakInside: "avoid",
									}}
								>
									{/* REFERENCE LINE */}
									{/* <div className="absolute bottom-0 right-50 z-1 flex h-full w-4 origin-[left_100px] translate-y-0 rotate-[5deg] flex-row ">
									<div className={`w-1`} />
									<div className={`w-1 bg-dipai-primary-501`} />
									<div className={`w-2 bg-dipai-secondary-901`} />
								</div> */}
									{/* HEADER ELEMENT */}
									<props.ReportPageHeader
										customerLogo={props.customerLogo}
										dipaiLogo={props.dipaiLogo}
										reportName={props.reportName}
										dateGenerated={dateStrLocal}
										dateFrom={props.dateFrom}
										dateTo={props.dateTo}
										listOfVessels={props.listOfVessels}
									/>
									{/* BODY ELEMENT  */}
									<props.ReportPageBody>
										{pageContent.map((layout) => renderComponentLayout(layout))}
									</props.ReportPageBody>
									{/* FOOTER ELEMENT  */}
									<props.ReportPageFooter
										currentPage={idx + 1}
										totalPages={totalPages}
									/>
								</div>
							))}
						</>
					) : null}
				</div>
			</div>
			{TriggerOnLoadingElement !== undefined && isDataLoading === true
				? TriggerOnLoadingElement
				: null}
			{TriggerElement !== undefined && isDataLoading === false
				? cloneElement(TriggerElement, {
						onClick: props.isDisabled !== true ? handlePrintOnClick : undefined,
					})
				: null}
		</>
	)
})

const ReportGeneratorNamespace = Object.assign(ReportGenerator, {
	ContentWrapper: ContentWrapper,
	Trigger: ReportGeneratorTrigger,
	TriggerOnLoading: ReportGeneratorTriggerOnLoading,
})
export { ReportGeneratorNamespace as ReportGenerator }
